/**
 * useConflicts
 *
 * Live list of sync_queue rows in `conflict` status. Powers the ConflictModal
 * queue — the modal shows them one at a time and calls
 * resolveKeepMine / resolveUseServer to drain the list.
 */

import { useEffect, useState, useCallback } from "react";

import { db } from "../db/schema";
import {
  resolveKeepMine,
  resolveUseServer,
  subscribe,
} from "../services/sync-push.engine";
import type { SyncQueueOnDisk } from "../db/db.types";

export interface ConflictsApi {
  conflicts:        SyncQueueOnDisk[];
  current:          SyncQueueOnDisk | null;
  keepMine:         () => Promise<void>;
  useServerVersion: () => Promise<void>;
}

export function useConflicts(): ConflictsApi {
  const [conflicts, setConflicts] = useState<SyncQueueOnDisk[]>([]);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const rows = await db.sync_queue.where("status").equals("conflict").toArray();
      if (cancelled) return;
      // Stable order so the modal doesn't shuffle as background resolves arrive.
      rows.sort((a, b) => a.client_timestamp.localeCompare(b.client_timestamp));
      setConflicts(rows);
    };
    refresh();
    const unsub = subscribe(refresh);
    return () => { cancelled = true; unsub(); };
  }, []);

  const current = conflicts[0] ?? null;

  const keepMine = useCallback(async () => {
    if (!current) return;
    await resolveKeepMine(current.id);
  }, [current]);

  const useServerVersion = useCallback(async () => {
    if (!current) return;
    await resolveUseServer(current.id);
  }, [current]);

  return { conflicts, current, keepMine, useServerVersion };
}
